import { RadioGroup, RadioGroupItem } from "@/components/ui/RadioGroup";
import { useTranslation } from "@/hooks/useTranslation";
import { pickLocalized } from "@/lib/localized";

// ─────────────────────────────────────────────
// WeightedMultipleChoiceQuestion
//
// Radio group from question options.
// Design: §6.5 Question Type → WEIGHTED_MULTIPLE_CHOICE
// Supports both choices array format and flat object format.
// ─────────────────────────────────────────────

export interface ChoiceOption {
  value: string;
  label: string;
  labelSr?: string;
  score: number;
}

interface WeightedMultipleChoiceQuestionProps {
  questionId: string;
  options: ChoiceOption[];
  value: string | null;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export function WeightedMultipleChoiceQuestion({
  questionId,
  options,
  value,
  onChange,
  readOnly,
}: WeightedMultipleChoiceQuestionProps) {
  const { language } = useTranslation();
  if (!options || options.length === 0) {
    return (
      <p className="text-[13px] text-text-muted italic">No options defined.</p>
    );
  }

  return (
    <RadioGroup
      value={value ?? ""}
      onValueChange={readOnly ? undefined : onChange}
      className="flex flex-col gap-2"
    >
      {options.map((opt) => (
        <div key={opt.value} className="flex items-center gap-2">
          <RadioGroupItem
            value={opt.value}
            id={`${questionId}-${opt.value}`}
            disabled={readOnly}
          />
          <label
            htmlFor={`${questionId}-${opt.value}`}
            className="cursor-pointer text-[14px] text-text-secondary"
          >
            {pickLocalized(opt.label, opt.labelSr, language)}
          </label>
        </div>
      ))}
    </RadioGroup>
  );
}
